import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Rich hover card for register subject column (project + subject). Uses a portal so it is not clipped by table overflow.
 */
const CorrespondenceSubjectTooltip = ({ projectName, subject, children }) => {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ left: 0, top: 0 });

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cardMaxW = 400;
    const left = Math.max(12, Math.min(r.left, window.innerWidth - cardMaxW - 12));
    const top = r.top - 10;
    setCoords({ left, top });
  }, []);

  const show = useCallback(() => {
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const hide = useCallback(() => {
    setVisible(false);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const onReposition = () => hide();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [visible, hide]);

  const card = visible && (
    <div
      className="correspondence-subject-tooltip-card correspondence-subject-tooltip-card--floating"
      style={{
        position: 'fixed',
        left: coords.left,
        top: coords.top,
      }}
      role="tooltip"
    >
      <div className="correspondence-subject-tooltip-arrow" aria-hidden />
      <div className="correspondence-subject-tooltip-section">
        <span className="correspondence-subject-tooltip-kicker">Project</span>
        <p className="correspondence-subject-tooltip-title project-name-highlight--tooltip">{projectName || '—'}</p>
      </div>
      <div className="correspondence-subject-tooltip-divider" />
      <div className="correspondence-subject-tooltip-section">
        <span className="correspondence-subject-tooltip-kicker">Subject</span>
        <p className="correspondence-subject-tooltip-body">{subject || '—'}</p>
      </div>
    </div>
  );

  return (
    <>
      <span
        ref={anchorRef}
        className="correspondence-subject-tooltip-anchor"
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {children}
      </span>
      {typeof document !== 'undefined' && createPortal(card, document.body)}
    </>
  );
};

export default CorrespondenceSubjectTooltip;
